<template>
    <div class="modal fade" ref="modalConflicts" id="conflictsModal" tabindex="-1" role="dialog"
        aria-labelledby="conflictsModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title fs-5" id="conflictsModalLabel">
                        <i class="fa fa-exclamation-triangle text-warning me-2"></i>
                        CONFLICTOS DETECTADOS
                    </h5>
                    <button class="btn-close" type="button" @click="conflictsModal.hide()" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div class="mb-4">
                        <div class="d-flex align-items-center">
                            <div class="row">
                                <strong class="col-12 h4">Evento: {{ eventData.title }}</strong>
                                <div class="col-sm-5 col-12 h5">
                                    <small class="text-muted">Fecha: {{ formatDate(eventData.init_date) }} - {{ formatDate(eventData.end_date) }}</small>
                                </div>
                                <div class="col-sm-2 col-12 h5">
                                    <small class="text-muted">Hora: {{ eventData.hour }}</small>
                                </div>
                                <div class="col-sm-5 col-12 h5">
                                    <small class="text-muted">Lugar: {{ eventData.place?.description }}</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mb-3 h6">
                        <p class="mb-2">
                            <strong>Se encontraron {{ conflictsList.length }} conflicto(s):</strong>
                        </p>
                        <small class="text-muted">
                            IMPORTANTE: Solo se verifican eventos ya registrados en el sistema.
                        </small>
                    </div>

                    <div class="conflicts-container" style="max-height: 400px; overflow-y: auto">
                        <div v-for="(conflict, index) in conflictsList" :key="conflict.eventId"
                            class="conflict-item mb-3 p-3 border rounded">

                            <div class="d-flex justify-content-between align-items-start mb-2">
                                <div class="flex-grow-1">
                                    <h6 class="mb-1 fw-bold">
                                        <i class="fa fa-calendar me-1"></i>
                                        {{ conflict.eventTitle }}
                                    </h6>
                                    <div class="conflict-type mb-2">
                                        <span class="conflict-description">{{ conflict.conflictType }}</span>
                                    </div>
                                </div>
                                <div class="conflict-severity-icon">
                                    <i :class="getSeverityIcon(conflict.severity)"
                                        :style="{ color: getSeverityColor(conflict.severity) }"></i>
                                </div>
                            </div>

                            <div class="conflict-details">
                                <div class="row text-sm">
                                    <div class="col-md-6">
                                        <div class="detail-item">
                                            <i class="fa fa-calendar-o me-1 text-muted"></i>
                                            <strong>Fecha:</strong> {{ formatDate(conflict.startDate) }} - {{
                                                formatDate(conflict.endDate) }}
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="detail-item">
                                            <i class="fa fa-clock-o me-1 text-muted"></i>
                                            <strong>Hora:</strong> {{ conflict.hour }}
                                        </div>
                                    </div>
                                    <div class="col-12 mt-1">
                                        <div class="detail-item">
                                            <i class="fa fa-map-marker me-1 text-muted"></i>
                                            <strong>Lugar:</strong> {{ conflict.place }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-4 p-3 rounded">
                        <h4 class="mb-2">
                            <i class="fa fa-bar-chart me-1"></i>
                            Resumen de Conflictos
                        </h4>
                        <div class="row">
                            <div class="col-md-4" v-if="conflictSummary.critical > 0">
                                <div class="text-center">
                                    <div class="h4 text-danger fw-bold">{{ conflictSummary.critical }}</div>
                                    <small class="text-muted">Críticos</small>
                                </div>
                            </div>
                            <div class="col-md-4" v-if="conflictSummary.high > 0">
                                <div class="text-center">
                                    <div class="h4 text-warning fw-bold">{{ conflictSummary.high }}</div>
                                    <small class="text-muted">Altos</small>
                                </div>
                            </div>
                            <div class="col-md-4" v-if="conflictSummary.medium > 0">
                                <div class="text-center">
                                    <div class="h4 text-info fw-bold">{{ conflictSummary.medium }}</div>
                                    <small class="text-muted">Medios</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="mt-3 alert alert-info text-dark h4">
                        <h6 class="mb-2">
                            <i class="fa fa-lightbulb-o me-1"></i>
                            Recomendación
                        </h6>
                        <p class="mb-0">{{ getRecommendationText() }}</p>
                    </div>
                </div>

                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="conflictsModal.hide()">
                        <i class="fa fa-times me-1"></i>
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { Modal } from "bootstrap";

const props = defineProps({
    event: {
        type: Object,
        default: () => ({}),
    },
    conflicts: {
        type: Array,
        default: () => [],
    },
});

const modalConflicts = ref(null);
const conflictsModal = ref(null);

const eventData = computed(() => props.event || {});
const conflictsList = computed(() => props.conflicts || []);

const conflictSummary = computed(() => {
    const summary = { critical: 0, high: 0, medium: 0 };

    conflictsList.value.forEach(conflict => {
        switch (conflict.severity) {
            case 'error':
                summary.critical++;
                break;
            case 'warning':
                summary.high++;
                break;
            case 'info':
                summary.medium++;
                break;
        }
    });

    return summary;
});

const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
};

const getConflictItemClass = (severity) => {
    const classes = {
        error: "border-danger bg-danger bg-opacity-10",
        warning: "border-warning bg-warning bg-opacity-10",
        info: "border-info bg-info bg-opacity-10",
    };
    return classes[severity] || "border-secondary bg-light";
};

const getSeverityBadgeClass = (severity) => {
    const classes = {
        error: "bg-danger",
        warning: "bg-warning text-dark",
        info: "bg-info text-dark",
    };
    return classes[severity] || "bg-secondary";
};

const getSeverityLabel = (severity) => {
    const labels = {
        error: "CRÍTICO",
        warning: "ALTO",
        info: "MEDIO",
    };
    return labels[severity] || "DESCONOCIDO";
};

const getSeverityIcon = (severity) => {
    const icons = {
        error: "fa fa-times-circle fa-2x",
        warning: "fa fa-exclamation-triangle fa-2x",
        info: "fa fa-info-circle fa-2x",
    };
    return icons[severity] || "fa fa-question-circle fa-2x";
};

const getSeverityColor = (severity) => {
    const colors = {
        error: "#dc3545",
        warning: "#fd7e14",
        info: "#0dcaf0",
    };
    return colors[severity] || "#6c757d";
};

const getRecommendationAlertClass = () => {
    if (conflictSummary.value.critical > 0) {
        return "alert-danger";
    } else if (conflictSummary.value.high > 0) {
        return "alert-warning";
    } else {
        return "alert-info";
    }
};

const getRecommendationText = () => {
    if (conflictSummary.value.critical > 0) {
        return "Se recomienda NO aprobar este evento debido a conflictos críticos (mismo día, hora y lugar). Considere observar el evento para solicitar cambiar la fecha, hora o lugar.";
    } else if (conflictSummary.value.high > 0) {
        return "Se recomienda revisar cuidadosamente estos conflictos antes de aprobar. Los eventos comparten el mismo día y lugar, pero diferentes horarios.";
    } else {
        return "Los conflictos son de nivel medio. Las fechas se solapan pero en diferentes días. Se puede aprobar con precaución.";
    }
};

const show = () => {
    conflictsModal.value.show();
};

const hide = () => {
    conflictsModal.value.hide();
};

onMounted(() => {
    conflictsModal.value = new Modal(modalConflicts.value);
});

defineExpose({
    show,
    hide,
});
</script>

<style scoped>
.conflict-item {
    transition: all 0.2s ease;
}

.conflict-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.detail-item {
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
}

.conflict-description {
    font-size: 0.875rem;
    line-height: 1.4;
}

.conflicts-container {
    scrollbar-width: thin;
    scrollbar-color: #6c757d #f8f9fa;
}

.conflicts-container::-webkit-scrollbar {
    width: 6px;
}

.conflicts-container::-webkit-scrollbar-track {
    background: #f8f9fa;
    border-radius: 3px;
}

.conflicts-container::-webkit-scrollbar-thumb {
    background: #6c757d;
    border-radius: 3px;
}

.conflicts-container::-webkit-scrollbar-thumb:hover {
    background: #495057;
}

.text-sm {
    font-size: 0.875rem;
}

.bg-opacity-10 {
    --bs-bg-opacity: 0.1;
}

.conflict-severity-icon {
    min-width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-header {
    border-bottom: 2px solid #dee2e6;
}

.modal-footer {
    border-top: 2px solid #dee2e6;
}
</style>